import "./sharingscreen.css";
import { useEffect, useState } from "react";
import { useLanguage } from "../../context/LanguageContext";
import { useTenant } from "../../context/TenantContext";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import apiService from "../../services/api.service";
import Button from "../../components/ui/button";
import Loader from "../../components/ui/loader";
import DOMPurify from 'dompurify';
import {
    buildFallbackCaptionFromTranslator,
    getConfiguredSocialShareCaption,
} from "../../utils/gamificationSocialShare";

export default function SharingScreen() {
    const { state } = useLocation();
    const { reward, chances, campaignId, congratsImage, type, imageUrl, tnxId, campaignType, campaignConfig, winAnimations } = state || {};
    const [copied, setCopied] = useState(false);
    const [caption, setCaption] = useState('');
    const { tenant } = useTenant();
    const { t, languageId, languageCode } = useLanguage();
    const navigate = useNavigate();
    const { tenanturlname } = useParams();

    useEffect(() => {
        const sharingText =
            getConfiguredSocialShareCaption({
                campaignConfig,
                rewardAmount: reward,
                languageId,
                languageCode,
            }) || buildFallbackCaptionFromTranslator(t, reward);
        setCaption(sharingText);
    }, [campaignConfig, languageCode, languageId, reward, t]);

    const executeAction = async () => {
        if (type === 'instagram') {
            window.open(`https://www.instagram.com/`, '_blank');
        }
        if (type === 'twitter') {
            const twitterShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}`;
            window.open(twitterShareUrl, '_blank');
        }
        await addSocialShareDetails();
    };

    const handleDownload = async () => {
        if (!imageUrl) return;

        const sanitizedUrl = DOMPurify.sanitize(imageUrl);

        try {
            const urlObject = new URL(sanitizedUrl);
            if (urlObject.protocol !== 'http:' && urlObject.protocol !== 'https:') {
                throw new Error('Invalid URL protocol');
            }

            // Fetch directly from the image URL
            const response = await fetch(sanitizedUrl);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = 'VisaWariChanceCashback.png';

            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download failed:', error);
        }
    };

    const addSocialShareDetails = async () => {
        try {
            const info = {
                tnxId: tnxId,
                tenantid: tenant?.tenantid,
                platform: type === 'twitter' ? "X" : type,
                analytics: {
                    action: "Social Share",
                    externaluserid: "",
                    tenantid: tenant?.tenantid,
                }
            };
            await apiService.post("/api/gamification/add-social-share-details", info);
        } catch (error) {
            console.error("Error adding social share details:", error);
        }
    };

    if (!state) {
        return <Loader fullscreen label={t("homePage.loading")} size="xl" />;
    }

    return (
        <div style={{ minHeight: '100vh', zIndex: 99, margin: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div className="align-center" style={{ marginTop: '10px', marginBottom: '10px' }}>
                <p style={{ fontSize: '18px', color: '#000000', fontWeight: 'bold', textAlign: 'center' }}>
                    {t("gamification.sharePostTitle") || "Share Your Win!"}
                </p>
            </div>
            <div className="bg-gray-100 p-4 rounded" style={{ paddingBottom: copied ? '20px' : '10px', maxWidth: '600px', width: '100%', marginBottom: '10px' }}>
                <p
                    style={{ color: 'black', textAlign: 'center', whiteSpace: 'pre-line' }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(caption) }}
                />
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            window.dataLayer = window.dataLayer || [];
                            window.dataLayer.push({ event: 'cta_click', ctaName: 'copy_share_link' });
                            navigator.clipboard.writeText(caption);
                            setCopied(true);
                            setTimeout(() => {
                                setCopied(false);
                            }, 2500);
                        }}
                    >
                        {t("gamification.copy") || "Copy"}
                    </Button>
                    {copied && (
                        <p className="font-11" style={{ color: 'green', textAlign: 'center', margin: 0 }}>
                            {t("gamification.copiedToClipboard") || "Copied to clipboard!"}
                        </p>
                    )}
                </div>
            </div>
            <div className="align-center" style={{ maxWidth: '600px', width: '100%' }}>
                <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                    {imageUrl && <img src={imageUrl} alt="Reward" style={{ maxWidth: "100%", height: "auto", display: 'block', margin: '0 auto' }} />}
                </div>
                <div style={{ marginTop: '10px', marginBottom: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Button
                        variant="secondary"
                        onClick={handleDownload}
                        style={{
                            backgroundColor: '#000000',
                            color: '#FFFFFF',
                            borderRadius: '25px',
                            border: 'none'
                        }}
                    >
                        ↓ {t("gamification.downloadImage") || "Download Image"}
                    </Button>
                </div>
                <div style={{ marginTop: '20px', marginBottom: '10px' }}>
                    <p style={{ color: 'black', textAlign: 'center', marginBottom: '10px' }}>
                        {t("gamification.sharingInstruction1") || "Download the image and share it on your social media"}<br />
                        {t("gamification.sharingInstruction2") || "Don't forget to tag us!"}
                    </p>
                    <div style={{ width: '100%', paddingLeft: '20px', paddingRight: '20px', display: 'flex', justifyContent: 'center' }}>
                        <Button variant="primary" onClick={executeAction} style={{ borderRadius: '25px', width: '100%' }}>
                            {t("gamification.sharePost") || "Share Post"}
                        </Button>
                    </div>
                </div>
                <div style={{ marginTop: '20px', marginBottom: '10px', textAlign: 'center' }}>
                    <Button
                        variant="link"
                        onClick={() => {
                            navigate(`/${tenanturlname}/gamification/congratulation`, {
                                state: {
                                    reward: reward,
                                    chances: chances,
                                    campaignId: campaignId,
                                    congratsImage: congratsImage,
                                    tnxId: tnxId,
                                    imageUrl: imageUrl,
                                    campaignType: campaignType,
                                    campaignConfig: campaignConfig,
                                    winAnimations: winAnimations || []
                                }
                            });
                        }}
                        style={{ color: '#838181' }}
                    >
                        {t("gamification.goBack") || "Go Back"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
